import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, BACKEND_URL } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ArrowLeft, Upload, Award, CheckCircle } from 'lucide-react';


const ACHIEVEMENT_CATEGORIES = {
  'co-curricular': ['Internship', 'Papers Published', 'Society Membership'],
  'extra-curricular': ['Cultural Events', 'Competitions', 'Sports', 'Clubs Joined', 'NSS', 'NCC', 'Civil Defense', 'Amateur Radio', 'Other'],
  'project': ['Technical Project', 'Research Project', 'Innovation'],
  'certification': ['Online Course', 'Professional Certification', 'Workshop'],
  'training': ['Placement Training', 'Skill Development', 'Industrial Training']
};

function AchievementForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    title: '',
    description: '',
    date: ''
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const studentSession = JSON.parse(sessionStorage.getItem('studentSession') || '{}');
  const rollNumber = studentSession.rollNumber;

  const handleCategoryChange = (e) => {
    setFormData({
      ...formData,
      category: e.target.value,
      subcategory: ''
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file size (50KB = 51200 bytes)
    if (selectedFile.size > 51200) {
      setError('File size must be less than 50 KB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Only JPG, PNG, or PDF files are allowed');
      return;
    }

    setFile(selectedFile);
    setError('');

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview('PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please upload a supporting document');
      return;
    }

    if (!rollNumber) {
      setError('Session expired. Please login again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload file to backend server (local uploads folder)
      const formDataToSend = new FormData();
      // IMPORTANT: Fields must come BEFORE file for multer to read them
      formDataToSend.append('rollNumber', rollNumber);
      formDataToSend.append('category', 'achievements');
      formDataToSend.append('file', file); // This will create uploads/achievements/{rollNumber}/

      const uploadResponse = await fetch(`${BACKEND_URL}/api/upload/achievement`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const uploadData = await uploadResponse.json();
      
      const documentUrl = uploadData.fileUrl || uploadData.publicUrl || uploadData.filePath;

      if (!uploadData.success || !documentUrl) {
        throw new Error('Invalid response from server');
      }

      // Create achievement document with local file path
      const achievementData = {
        rollNumber: rollNumber,
        studentName: studentSession.name,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        date: formData.date,
        documentUrl: documentUrl,
        documentPath: uploadData.filePath || null,
        driveFileId: uploadData.fileId || null,
        status: 'pending',
        verifiedBy: null,
        verificationDate: null,
        rejectionReason: null,
        submittedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'achievements'), achievementData);

      setSubmitted(true);
      setTimeout(() => {
        navigate(-1);
      }, 3000);

    } catch (err) {
      console.error('Error submitting achievement:', err);
      setError('Failed to submit achievement. Please try again. Make sure both frontend and backend servers are running (npm run dev).');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Achievement Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Your achievement will be verified by your mentor. Thanks for your patience.
          </p>
          <div className="animate-pulse text-sm text-gray-500">
            Redirecting to dashboard...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Award className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Add Achievement</h1>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={handleCategoryChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Category</option>
                <option value="co-curricular">Co-curricular Activities</option>
                <option value="extra-curricular">Extra-curricular Activities</option>
                <option value="project">Projects Done</option>
                <option value="certification">Certification Courses</option>
                <option value="training">Training Related to Placement</option>
              </select>
            </div>

            {/* Subcategory Selection */}
            {formData.category && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory *
                </label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Subcategory</option>
                  {ACHIEVEMENT_CATEGORIES[formData.category].map(subcat => (
                    <option key={subcat} value={subcat}>{subcat}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Achievement Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Completed Python Programming Internship at TCS"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your achievement in detail..."
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Achievement *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supporting Document * (Max 50 KB)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  {!preview ? (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Drag & Drop or Click to Browse</p>
                      <p className="text-sm text-gray-500">JPG, PNG, or PDF (Max 50 KB)</p>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="mt-4 inline-block px-6 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        Select File
                      </label>
                    </>
                  ) : (
                    <div>
                      {preview === 'PDF' ? (
                        <div className="bg-gray-100 p-4 rounded">
                          <p className="text-gray-700 font-medium">{file.name}</p>
                          <p className="text-sm text-gray-500">PDF Document</p>
                        </div>
                      ) : (
                        <img
                          src={preview}
                          alt="Preview"
                          className="max-w-full h-48 object-contain mx-auto rounded cursor-pointer"
                          onClick={() => window.open(preview, '_blank')}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          setPreview(null);
                        }}
                        className="mt-4 text-red-600 hover:text-red-700"
                      >
                        Remove File
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                File size: {file ? `${(file.size / 1024).toFixed(2)} KB` : '0 KB'} / 50 KB
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Achievement'}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Info Message */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Your achievement will be verified by your mentor. 
              Once verified, you won't be able to edit or delete it. 
              Please ensure all information is accurate before submitting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AchievementForm;
